import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { memo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Drawer from 'src/components/global/Drawer/Drawer'
import { Section } from 'src/components/global/Overview/Section'
import { THEME } from 'src/config/config'
// import { ReviewsContainer } from 'src/components/ReviewsContainer/ReviewsContainer'
import {
  FOUNDERS_EXECUTIVE_TEAM,
  FUNDING_DETAILS,
  fundingAmountsC,
  fundingInfosC,
  OPEN_TO_INVESTMENT,
} from 'src/constants/constants'
import { IAlspSolutionDataFragment, ISimilarSolutionItemFragment } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { AppState } from 'src/store/types'
import { CompareInfos } from 'src/store/types/compare'
import { DETAILS_TAB, FeaturesType, PRODUCT_BRIEFING_TAB, SectionItem, VENDOR_ANALYSIS_TAB } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'
import { getFeaturesItems } from 'src/utils/getFeaturesItems'
import { getYearFoundedItem } from 'src/utils/getYearFoundedItem'
import { canViewPaywalledSolutionFields } from 'src/utils/solutionPaywall'

import IconBusiness from '../../assets/icon_business.svg'
import IconLawFirms from '../../assets/icon_law_firms.svg'
import { ThemeOnly } from '../global/ThemeOnly'
import { RelatedContents } from '../Partials/RelatedContents/RelatedContents'
import SkeletonLoading from '../SkeletonLoading/SkeletonLoading'
// import { ArticleCart } from '../ArticleCart/ArticleCart'
// import { CompareModalContent } from '../CompareModalContent/CompareModalContent'
// import { SimilarSolutions } from '../SimilarSolutions/SimilarSolutions'
import { SolutionFounderItem } from '../SolutionFounderItem/SolutionFounderItem'
import SolutionVendorAnalysisContainer from '../SolutionsPage/SolutionVendorAnalysisContainer/SolutionVendorAnalysisContainer'

interface AlspDetailContainerProps {
  className?: string
  alsp: IAlspSolutionDataFragment
  tabIdx: number
  similars: ISimilarSolutionItemFragment[]
  analysisTotal: number
  productBriefingTotal: number
  relatedContent?: any[]
  totalRelatedContent?: number
}

const CompareModalContent = dynamic(
  () => import('../CompareModalContent/CompareModalContent').then((mod) => mod.CompareModalContent),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ArticleCart = dynamic(() => import('../ArticleCart/ArticleCart').then((mod) => mod.ArticleCart), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SimilarSolutions = dynamic(
  () => import('../SimilarSolutions/SimilarSolutions').then((mod) => mod.SimilarSolutions),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
// const ReviewsContainer = dynamic(
//   () => import('src/components/ReviewsContainer/ReviewsContainer').then((mod) => mod.ReviewsContainer),
//   {
//     loading: () => <SkeletonLoading className="bg-navy" />,
//   },
// )

/**
 * Primary UI component for user interaction
 */
export const AlspDetailContainer: React.FC<AlspDetailContainerProps> = memo(
  ({
    className,
    alsp,
    tabIdx,
    similars,
    analysisTotal,
    productBriefingTotal,
    relatedContent = [],
    totalRelatedContent = 0,
  }) => {
    const url = alsp?.website || ''
    const productType = alsp?.productType?.toLocaleLowerCase() || ''
    const consolidationData: any = alsp?.consolidationData || null
    const hqs = alsp?.hqs || []
    const regionsServed = alsp?.regionsServed || []
    const languages = alsp?.languages || []
    const offices = alsp?.offices || []
    const features = alsp?.features || []
    const yearFounded = alsp?.yearFounded
    const audiences = alsp?.alspData?.audiences || []
    const serviceOfferings = alsp?.subTopics || []
    const partners = alsp?.alspData?.partners || ''
    const size = alsp?.alspData?.size || 0
    const linkedin = alsp?.linkedin || ''
    const twitter = alsp?.twitter || ''
    const [isVisible, setIsVisible] = React.useState(false)
    const closeDrawer = React.useCallback(() => setIsVisible(false), [])
    const compareInfos: CompareInfos = useSelector((state: AppState) => state.compareInfos)
    const { session } = useAuthenticated()
    const confirmed = session?.user.confirmed || false
    const fundingInfo = alsp?.alspData?.fundingInfo
    const fundingAmount = alsp?.alspData?.fundingAmount
    const founders = alsp?.founders || []
    const alspData: any = alsp?.alspData
    const isDiverseLeadership: any = alsp?.isDiverseLeadership || false
    const fundingDetails = alsp?.alspData?.fundingDetails || null
    const openToInvestment = alsp?.alspData?.openToInvestment || false
    const companyType = session?.user?.companyType || ''
    const isPremiumUser = session?.user?.isPremium || false
    const unlockPaywalledContent = canViewPaywalledSolutionFields(!!session?.user?.isPremium, alsp, session?.user?.id)

    let showOpenToInvestment = false

    if (companyType.toLocaleLowerCase() === 'investor') {
      showOpenToInvestment = true
    }

    useEffect(() => {
      if (compareInfos?.solutions?.length > 0 && confirmed) {
        if (compareInfos.solutions.length === 1) {
          // const compare: CompareSolution = compareInfos.solutions[0]
          /* if (compare.slug !== alsp?.slug) {
                          setIsVisible(true)
                        } else {
                          setIsVisible(false)
                        } */
          setIsVisible(true)
        } else {
          setIsVisible(true)
        }
      } else {
        setIsVisible(false)
      }
    }, [compareInfos])

    const overviewItems: SectionItem[] = []
    if (checkNotEmpty(hqs)) {
      const convertedItems: FeaturesType[] =
        hqs?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = {
        title: 'HEADQUARTERS',
        type: 'SpanDataIcon',
        subType: 'normal',
        items: convertedItems,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(linkedin)) {
      const displayName = alsp?.displayName || 'Company'
      const title = `View ${displayName} on LinkedIn`
      const item: SectionItem = { type: 'LinkedinIconLink', items: [linkedin!], title }
      overviewItems.push(item)
    }
    if (checkNotEmpty(languages)) {
      const convertedItems: FeaturesType[] =
        languages?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'LANGUAGES', type: 'SpanDataList', subType: 'normal', items: convertedItems }
      overviewItems.push(item)
    }
    if (checkNotEmpty(regionsServed)) {
      const convertedItems: FeaturesType[] =
        regionsServed?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = {
        title: 'REGIONS SERVED',
        type: 'SpanDataList',
        subType: 'normal',
        items: convertedItems,
      }
      overviewItems.push(item)
    }
    if (checkNotEmpty(offices)) {
      const convertedItems: FeaturesType[] =
        offices?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []
      const item: SectionItem = { title: 'OFFICES', type: 'SpanDataList', subType: 'normal', items: convertedItems }
      overviewItems.push(item)
    }
    if (checkNotEmpty(twitter)) {
      const displayName = alsp?.displayName || 'Company'
      const title = `View ${displayName} on Twitter`
      const item: SectionItem = { type: 'TwitterIconLink', items: [twitter!], title }
      overviewItems.push(item)
    }

    const serviceItems: SectionItem[] = []
    if (checkNotEmpty(audiences)) {
      const convertedItems: FeaturesType[] =
        (audiences &&
          audiences?.map((item) => {
            const name = item?.name || ''
            let icon = ''
            if (name === 'Law Firms') icon = IconLawFirms
            else if (name === 'Corporate Legal') icon = IconBusiness
            const converted: FeaturesType = { name: name, icon: icon }
            return converted
          })) ||
        []
      const item: SectionItem = { title: 'CUSTOMER TYPE', type: 'SpanDataList', subType: 'cart', items: convertedItems }
      serviceItems.push(item)
    }
    if (checkNotEmpty(serviceOfferings)) {
      const convertedItems: FeaturesType[] =
        serviceOfferings?.filter((item) => !!item?.name).map((item) => ({ name: item?.name || '' })) || []

      const item: SectionItem = {
        title: 'SERVICE OFFERINGS',
        type: 'DottedDataList',
        subType: '',
        items: convertedItems,
      }
      serviceItems.push(item)
    }
    if (checkNotEmpty(alspData?.valueProposition)) {
      const item: SectionItem = {
        title: 'Value Proposition',
        type: 'DottedDataList',
        subType: '',
        items: alspData?.valueProposition ? [{ name: alspData?.valueProposition }] : [],
        sValue: alspData?.valueProposition || '',
        isFullWidth: true,
      }
      serviceItems.push(item)
    }

    const experItems: SectionItem[] = []
    if (checkNotEmpty(yearFounded)) {
      const yearFoundedItem = getYearFoundedItem('OPERATING SINCE', yearFounded)
      if (yearFoundedItem) {
        experItems.push(yearFoundedItem)
      }
    }
    if (checkNotEmpty(partners)) {
      const item: SectionItem = { title: 'PARTNERS', type: 'string', subType: '', items: [], sValue: partners || '' }
      experItems.push(item)
    }
    if (checkNotEmpty(size)) {
      const item: SectionItem = {
        title: 'NUMBER OF CONSULTANTS',
        type: 'number',
        subType: '',
        items: [],
        nValue: size || 0,
      }
      experItems.push(item)
    }
    if (checkNotEmpty(founders)) {
      const foundersItem: SectionItem = {
        title: FOUNDERS_EXECUTIVE_TEAM, // 'Founders/Executive Team',
        type: 'CustomList',
        subType: '',
        items: founders || [],
        isFullWidth: true,
        isDiverseLeadership: isDiverseLeadership,
      }
      experItems.push(foundersItem)
    }
    if (checkNotEmpty(fundingInfo)) {
      const item: SectionItem = {
        title: 'Latest Funding Round',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingInfosC.find((item: any) => item.value === fundingInfo)?.label }],
      }
      overviewItems.push(item)
    }

    if (checkNotEmpty(fundingAmount)) {
      const item: SectionItem = {
        title: 'Total Funding Amount',
        type: 'DottedDataList',
        subType: 'more',
        items: [{ name: fundingAmountsC.find((item: any) => item.value === fundingAmount)?.label }],
      }
      overviewItems.push(item)
    }

    if (THEME === 'legaltech' && checkNotEmpty(fundingDetails)) {
      const item: SectionItem = {
        title: FUNDING_DETAILS,
        type: 'string',
        subType: '',
        items: [],
        sValue: fundingDetails || '',
        isFullWidth: true,
      }
      overviewItems.push(item)
    }
    if (
      THEME === 'legaltech' &&
      openToInvestment &&
      ((showOpenToInvestment && isPremiumUser) || canViewPaywalledSolutionFields(false, alsp, session?.user?.id))
    ) {
      const item: SectionItem = {
        title: 'OPEN_TO_INVESTMENT',
        type: 'CheckDataList',
        isBold: true,
        isFullWidth: true,
        items: [{ name: OPEN_TO_INVESTMENT, isChecked: true }],
      }
      overviewItems.push(item)
    }

    const featuresItems: SectionItem[] = getFeaturesItems(
      serviceOfferings?.filter((item) => item != null) || [],
      features?.filter((item) => item != null) || [],
    )
    const showWebsite =
      productType === 'graveyard' || (productType === 'consolidation' && consolidationData?.enableInActiveDir === false)
        ? false
        : true

    const randomNum = alsp?.slug ? alsp.slug : Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    return (
      <div className={cn(className, 'flex flex-col justify-center w-full px-5')}>
        {compareInfos.solutions && compareInfos.solutions.length ? (
          <Drawer duration={250} hideScrollbars={true} onClose={closeDrawer} isVisible={isVisible}>
            <CompareModalContent onClose={closeDrawer} solutionType={compareInfos.solutions[0].solutionType} />
          </Drawer>
        ) : null}
        <>
          {tabIdx === DETAILS_TAB && (
            <div className="flex flex-col justify-center w-full">
              <div className="pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full flex md:flex-row flex-col py-2">
                <div
                  className={`flex flex-col mb-auto ${similars.length > 0 || relatedContent?.length > 0 ? 'md:w-[50rem] md:mr-[2.3125rem]' : 'w-full'} w-full`}
                >
                  <Section
                    key={randomNum + 'AlspDetailContainer-Overview'}
                    title={'Overview'}
                    description={alsp?.longDescription || ''}
                    url={!showWebsite ? '' : url || ''}
                    items={overviewItems}
                    unlockPaywalledContent={unlockPaywalledContent}
                  />
                  {featuresItems.length > 0 && (
                    <Section
                      key={randomNum + 'AlspDetailContainer-Features'}
                      title={'Features'}
                      description={''}
                      url={''}
                      items={featuresItems}
                      unlockPaywalledContent={unlockPaywalledContent}
                    />
                  )}
                  {serviceItems.length > 0 && (
                    <Section
                      key={randomNum + 'AlspDetailContainer-FeatServicesures'}
                      title={'Services'}
                      description={''}
                      url={''}
                      items={serviceItems}
                      unlockPaywalledContent={unlockPaywalledContent}
                    />
                  )}
                  {experItems.length > 0 && (
                    <Section
                      key={randomNum + 'AlspDetailContainer-Experience'}
                      title={'Experience'}
                      description={''}
                      url={''}
                      items={experItems}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      renderItem={(founder: any) => (
                        <SolutionFounderItem
                          key={randomNum + 'AlspDetailContainer-SolutionFounderItem'}
                          founder={founder}
                        />
                      )}
                      unlockPaywalledContent={unlockPaywalledContent}
                    />
                  )}
                  {checkNotEmpty(alsp?.articles) && (
                    <ArticleCart key={randomNum + 'AlspDetailContainer-ArticleCart'} articles={alsp?.articles || []} />
                  )}
                </div>

                {(similars.length > 0 || relatedContent?.length > 0) && (
                  <div className="flex flex-col md:w-[15rem] w-full h-full mb-auto">
                    <ThemeOnly theme="legaltech">
                      {relatedContent && (
                        <RelatedContents
                          contents={relatedContent}
                          slug={alsp?.slug ? 'alsps/' + alsp?.slug : ''}
                          totalRelatedContent={totalRelatedContent}
                        />
                      )}
                    </ThemeOnly>

                    {similars.length > 0 && (
                      <SimilarSolutions
                        key={randomNum + 'AlspDetailContainer-SimilarSolutions'}
                        similars={similars}
                        slug={alsp?.slug || ''}
                        originName={alsp?.displayName || ''}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* {tabIdx === REVIEWS_TAB && (
            <ReviewsContainer
              key={randomNum + 'ReviewsContainer'}
              reviews={alsp?.reviews?.filter((review) => review != null) || []}
              isShowSubmit={true}
              title={alsp?.displayName || ''}
              solutionType={IEnum_Solution_Type.Alsp}
              id={alsp.documentId || ''}
              isProgressing={false}
              isEnhanced={false}
            />
          )} */}
          {tabIdx === VENDOR_ANALYSIS_TAB && (
            <SolutionVendorAnalysisContainer
              key={randomNum + 'SolutionVendorAnalysisContainer'}
              solutionId={alsp.documentId || ''}
              analysisTotal={analysisTotal}
            />
          )}
          {tabIdx === PRODUCT_BRIEFING_TAB && (
            <>
              <SolutionVendorAnalysisContainer
                key={randomNum + '-AlspDetailContainer-SolutionProductBriefingContainer'}
                solutionId={alsp.documentId || ''}
                analysisTotal={productBriefingTotal}
                documentType="Product Briefing"
              />
            </>
          )}
        </>
      </div>
    )
  },
)
